import { z } from 'zod';
import {
  FACES,
  GENDERS,
  HAIRSTYLES,
  HAIR_COLORS,
  OUTFIT_COLORS,
  OUTFIT_STYLES,
  SECT_IDS,
  SKIN_TONES,
} from '@jianghu/game-data';

/** REST request schemas (account and character management). */

export const EmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Enter a valid email address.');

export const PasswordSchema = z
  .string()
  .min(10, 'Password must be at least 10 characters.')
  .max(200, 'Password is too long.');

export const RegisterSchema = z.object({ email: EmailSchema, password: PasswordSchema });
export const LoginSchema = z.object({ email: EmailSchema, password: z.string().min(1).max(200) });

/**
 * Accounts live in Firebase Authentication (shared with the other game on the same Firebase
 * project). The client signs in with Firebase, then exchanges the ID token for a game session.
 */
export const FirebaseLoginSchema = z.object({ idToken: z.string().min(1).max(4096) });

/** Character names: 3-16 letters (any script), digits or single spaces/underscores between words. */
export const CharacterNameSchema = z
  .string()
  .trim()
  .min(3, 'Name must be 3-16 characters.')
  .max(16, 'Name must be 3-16 characters.')
  .regex(/^[\p{L}\p{N}]+(?:[ _][\p{L}\p{N}]+)*$/u, 'Use letters and digits only (single spaces allowed).');

const oneOf = <T extends readonly [string, ...string[]]>(values: T) => z.enum(values);

export const AppearanceSchema = z.object({
  gender: oneOf(GENDERS),
  hairstyle: oneOf(HAIRSTYLES),
  hairColor: oneOf(HAIR_COLORS),
  face: oneOf(FACES),
  skinTone: oneOf(SKIN_TONES),
  outfitStyle: oneOf(OUTFIT_STYLES),
  outfitColor: oneOf(OUTFIT_COLORS),
});

export const CreateCharacterSchema = z.object({
  realmId: z.string().min(1).max(32),
  name: CharacterNameSchema,
  sect: oneOf(SECT_IDS),
  appearance: AppearanceSchema,
});

export const DeleteCharacterSchema = z.object({ confirmName: z.string().min(1).max(32) });

export type RegisterBody = z.infer<typeof RegisterSchema>;
export type LoginBody = z.infer<typeof LoginSchema>;
export type FirebaseLoginBody = z.infer<typeof FirebaseLoginSchema>;
export type CreateCharacterBody = z.infer<typeof CreateCharacterSchema>;

export interface MeResponse {
  user: { id: string; email: string; role: 'player' | 'admin' };
}

export interface FirebaseLoginResponse extends MeResponse {
  /**
   * The game session token. The session is also set as an HttpOnly cookie,
   * but browsers that block cross-site cookies (e.g. iOS ITP, when the client
   * and the server live on different domains) can present this token as an
   * Authorization: Bearer header / WebSocket query parameter instead.
   */
  sessionToken: string;
}

export interface RealmDTO {
  id: string;
  name: string;
  nameZh: string;
  region: string;
  online: number;
  capacity: number;
  status: 'online' | 'full' | 'offline';
}

export interface CharacterSummaryDTO {
  id: string;
  realmId: string;
  name: string;
  sect: string;
  level: number;
  zoneId: string;
  appearance: import('@jianghu/game-data').Appearance;
  weapon: string | null;
  armor: string | null;
  lastLoginAt: string | null;
}

export interface ApiErrorBody {
  error: { code: string; message: string; fields?: Record<string, string> };
}
